import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Group, NumberInput, Stack, Text, TextInput } from '@mantine/core';
import { IconClockHour4, IconInfoCircle } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { SectionCard } from './SectionCard';

interface Holat {
  lessonStart: string;
  lessonMinutes: number;
  breakMinutes: number;
  darslar: { no: number; startTime: string; endTime: string }[];
}

/**
 * DARS VAQTLARI.
 *
 * Ilgari jadvalda har bir dars uchun boshlanish va tugash vaqti QO'LDA
 * yozilardi — zerikarli ham, xato ham: ikki dars ustma-ust tushib qolardi va
 * buni faqat jadvalga qarab sezish mumkin edi.
 *
 * Endi bu yerda uchta qiymat belgilanadi, qolgani hisoblanadi. Natija DARHOL
 * ko'rinadi: admin "45 daqiqa" yozgach, 1-soat qachon tugashini o'ylab
 * o'tirmasdan ko'zi bilan tekshiradi.
 */
export function LessonTimeSettings() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ['lesson-times'],
    queryFn: async () => (await api.get<Holat>('/schedule/lesson-times')).data,
  });

  const [boshi, setBoshi] = useState('08:00');
  const [dars, setDars] = useState<number | string>(45);
  const [tanaffus, setTanaffus] = useState<number | string>(5);

  /* Serverdan kelgach maydonlar to'ldiriladi. Foydalanuvchi yozayotgan paytda
     ustiga yozib yubormasligi uchun faqat BIRINCHI kelishda. */
  const [toldirilgan, setToldirilgan] = useState(false);
  useEffect(() => {
    if (!data || toldirilgan) return;
    setBoshi(data.lessonStart);
    setDars(data.lessonMinutes);
    setTanaffus(data.breakMinutes);
    setToldirilgan(true);
  }, [data, toldirilgan]);

  const saqla = useMutation({
    mutationFn: () =>
      api.put('/schedule/lesson-times', {
        lessonStart: boshi,
        lessonMinutes: Number(dars),
        breakMinutes: Number(tanaffus),
      }),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('common.save') });
      /* Jadval formasidagi tanlov ro'yxati ham shu so'rovdan oziqlanadi. */
      qc.invalidateQueries({ queryKey: ['lesson-times'] });
      qc.invalidateQueries({ queryKey: ['schedule'] });
    },
    onError: (e) =>
      notifications.show({
        color: 'red',
        message:
          (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? t('common.error'),
      }),
  });

  return (
    <SectionCard title={t('lessonTimes.section')} subtitle={t('lessonTimes.sectionDesc')} icon={IconClockHour4} maw={520} mb="md">
      <Stack gap="sm">
        <Group grow align="flex-start">
          <TextInput
            label={t('lessonTimes.start')}
            placeholder="08:00"
            value={boshi}
            onChange={(e) => setBoshi(e.currentTarget.value)}
          />
          <NumberInput label={t('lessonTimes.duration')} min={5} max={180} value={dars} onChange={setDars} />
          <NumberInput label={t('lessonTimes.break')} min={0} max={120} value={tanaffus} onChange={setTanaffus} />
        </Group>

        {data && (
          /* NATIJA DARHOL: saqlashdan oldin ham nima chiqishini ko'rsatadi. */
          <Alert variant="light" color="blue" icon={<IconInfoCircle size={16} />} p="xs">
            <Group gap={6} wrap="wrap">
              {data.darslar.slice(0, 6).map((d) => (
                <Badge key={d.no} variant="light" size="sm">
                  {d.no}-soat {d.startTime}–{d.endTime}
                </Badge>
              ))}
            </Group>
          </Alert>
        )}

        <Group justify="space-between">
          <Text size="xs" c="dimmed">{t('lessonTimes.note')}</Text>
          <Button loading={saqla.isPending} onClick={() => saqla.mutate()}>{t('common.save')}</Button>
        </Group>
      </Stack>
    </SectionCard>
  );
}
